'use client';

import type { MouseEvent } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Badge } from '../../components/badge';
import { BadgeHttp } from '../../components/styled/badge-http';
import { Tooltip } from '../../components/styled/tooltip';
import { BodyDefault, BodyStrong } from '../../components/typography';
import { AssetTypeSchema } from '@terra/shared-schemas-orchestrator';
import type { EnrichedAsset } from '../../types';
import { ArchivedLifecycleBadge } from '../../components/archived-lifecycle-badge';

export type FindingAssetItem = Pick<
  EnrichedAsset,
  | 'type'
  | 'id'
  | 'method'
  | 'path'
  | 'url'
  | 'graphqlOperationName'
  | 'soapOperationName'
  | 'host'
  | 'port'
  | 'discoveredAssetType'
  | 'lifecycle'
>;

type FindingAssetProps = {
  assets?: FindingAssetItem[];
  onAssetClick?: (
    asset: FindingAssetItem,
    event: MouseEvent<HTMLElement>,
  ) => void;
};

export const getFindingAssetDisplayText = (asset: FindingAssetItem) => {
  if (asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET) {
    const host = asset.host || asset.url || asset.id;

    return asset.port ? `${host}:${asset.port}` : host;
  }

  const operationName =
    asset.soapOperationName ?? asset.graphqlOperationName ?? null;
  const baseValue = asset.path || asset.url || asset.id;

  if (operationName) {
    return `${baseValue}?${operationName}`;
  }

  return baseValue;
};

function FindingAssetRow({
  asset,
  onAssetClick,
  labelVariant = 'default',
}: {
  asset: FindingAssetItem;
  onAssetClick?: FindingAssetProps['onAssetClick'];
  labelVariant?: 'default' | 'strong';
}) {
  const tAssetsDisplay = useTranslations(
    'designSystem.findings.findingDetails.assetsDisplay',
  );
  const text = getFindingAssetDisplayText(asset);
  const Label = labelVariant === 'strong' ? BodyStrong : BodyDefault;
  const label = (
    <Label
      title={text || undefined}
      {...(labelVariant === 'default' && onAssetClick
        ? { truncate: true as const }
        : { wordBreak: 'break-all' as const })}
    >
      {text}
    </Label>
  );

  return (
    <HStack gap={2} alignItems="center" minW={0}>
      {asset.type === AssetTypeSchema.enum.ENDPOINT && asset.method && (
        <BadgeHttp method={asset.method} size="sm">
          {asset.method}
        </BadgeHttp>
      )}
      {asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET && (
        <Badge size="sm">{asset.discoveredAssetType ?? asset.type}</Badge>
      )}
      {asset.type === AssetTypeSchema.enum.WEBPAGE && (
        <Badge size="sm">{tAssetsDisplay('webpageSingular')}</Badge>
      )}
      {onAssetClick ? (
        <Box
          as="span"
          cursor="pointer"
          textDecoration="underline"
          textDecorationStyle="dotted"
          minW={0}
          onClick={(event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            onAssetClick(asset, event);
          }}
        >
          {label}
        </Box>
      ) : (
        label
      )}
      <ArchivedLifecycleBadge lifecycle={asset.lifecycle} />
    </HStack>
  );
}

export const FindingAsset = ({
  assets = [],
  onAssetClick,
}: FindingAssetProps) => {
  const t = useTranslations('designSystem.findings.findingAsset');

  if (assets.length === 0) {
    return (
      <HStack h="full" alignItems="center">
        <BodyDefault color="fg.muted">{t('noAsset')}</BodyDefault>
      </HStack>
    );
  }

  const [asset, ...rest] = assets;

  return (
    <HStack h="full" alignItems="center" minW={0}>
      {asset ? (
        <FindingAssetRow asset={asset} onAssetClick={onAssetClick} />
      ) : null}
      {rest.length > 0 && (
        <Tooltip
          positioning={{
            strategy: 'fixed',
            placement: 'top',
            offset: { mainAxis: 10 },
          }}
          title=""
          customContent={
            <VStack>
              {rest.map((restAsset) => (
                <FindingAssetRow
                  key={restAsset.id}
                  asset={restAsset}
                  onAssetClick={onAssetClick}
                  labelVariant="strong"
                />
              ))}
            </VStack>
          }
        >
          <Badge size="sm" cursor="pointer">
            {t('moreCount', { count: rest.length })}
          </Badge>
        </Tooltip>
      )}
    </HStack>
  );
};
